<template>
  <div>
    <nav class="w-full items-end justify-end pt-[1.625rem]" :aria-label="$t('aria_labels.main_navigation')">
      <!-- Dropdown Background -->
      <NavDropdownBackground :sub-nav-width="subNavWidth" :sub-nav-height="subNavHeight" />

      <ul id="main-nav" class="flex justify-between gap-x-8">
        <!-- LEVEL 1  -->
        <li
          v-for="item in main"
          :ref="item.page.id"
          :key="item.page.id"
        >
          <!-- Div or NuxtLink -->
          <div class="router-link">
            <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
            <Component
              :is="buttonOrNuxtLink(item.page.url)"
              :to="item.page.url && item.page.url"
              :target="item.page.url ? $useIsExternalUrl(item.page.url) ? '_blank' : '_self': null"
              class="font-semibold transition-colors ease-in-out typo-body hover:text-primary-900"
              :class="useIsRouterLinkActive(item.page.url, route.path)"
              :aria-expanded="item.children?.length ? selectedNavItem === item : null"
              :aria-controls="item.children?.length ? `sub-nav-${item.page.id}`: null"
              @keydown.space.prevent="item.children?.length ? selectedNavItem ? leave() : enter(item): null"
              @blur="item.children?.length ? leave() : null"
              @mouseenter="item.children?.length ? enter(item) : null"
              @mouseleave="item.children?.length ? leave(): null"
            >
              {{ item.page.title }}
            </Component>
          </div>

          <template v-if="item.children?.length">
            <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events -->
            <SubNavigation
              :id="`sub-nav-${item.page.id}`"
              :key="item.page.id"
              :show="selectedNavItem === item"
              :hide="!selectedNavItem"
              class="router-link-exact"
              :children="item.children"
              @has-focus-within="$event ? enter(item) : leave()"
              @mouseenter="enter(item)"
              @mouseleave="leave()"
            />
          </template>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script lang="ts" setup>
const route = useRoute()
const globals = inject(GlobalsKey, null)
const categories = inject('categoriesTree', ref([]))

const main = computed(() => {
  const mainNav = globals?.value.main?.tree || []

  // Find the on the first level the page with property is_categories_container = true and set its children to categories
  const categoriesContainer = mainNav.find(item => item.page.is_categories_container)
  if (categoriesContainer) {
    categoriesContainer.children = categories.value
  }

  return mainNav
})

const selectedNavItem = ref(null)
let enterTimeout: any = null
let leaveTimeout: any = null
const subNavWidth = ref(0)
const subNavHeight = ref(0)

async function enter(item: any) {
  if (enterTimeout === null) {
    enterTimeout = setTimeout(async () => {
      if (!item || !item.children?.length) {
        leave()
      }

      if (leaveTimeout !== null) {
        clearTimeout(leaveTimeout)
        leaveTimeout = null
      }

      selectedNavItem.value = item

      // Wait one tick until the sub navigation is rendered
      await nextTick()

      // Get the height of the sub navigation to set the height of the background
      const subNav = document.getElementById(`sub-nav-${item.page.id}`)
      subNavWidth.value = subNav?.offsetWidth || 0
      subNavHeight.value = subNav?.offsetHeight || 0
    }, 150)
  }
}

function leave() {
  if (leaveTimeout === null) {
    leaveTimeout = setTimeout(() => {
      selectedNavItem.value = null
      subNavWidth.value = 0
      subNavHeight.value = 0
    }, 300)
  }

  if (enterTimeout !== null) {
    clearTimeout(enterTimeout)
    enterTimeout = null
  }
}

// determine whether to load NuxtLink or button
function buttonOrNuxtLink(url: string) {
  if (url) {
    return resolveComponent('NuxtLink')
  }

  return 'button'
}

// watch on route changes
watch(route, () => {
  leave()
})
</script>
